import React from "react";
import { NavLink, Link } from "react-router-dom";
import { Activity, LayoutDashboard, BarChart3, LogOut, Sparkles, Trees, Users, BookOpen, ShieldCheck, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LevelBadge from "@/components/LevelBadge";
import { Avatar } from "@/components/Avatar";

const LINKS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-tab", end: true },
  { to: "/garden", label: "Garden", icon: Trees, testid: "nav-garden-tab" },
  { to: "/coach", label: "AI Coach", icon: Sparkles, testid: "nav-coach-tab" },
  { to: "/squad", label: "Squad", icon: Users, testid: "nav-squad-tab" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics-tab" },
  { to: "/guide", label: "How To", icon: BookOpen, testid: "nav-guide-tab" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();

  return (
    <>
      <aside className="w-64 fixed left-0 top-0 h-screen bg-[#0D0F17] border-r border-[#242417] z-40 hidden md:flex flex-col p-5">
        <div className="flex items-center gap-3 px-2 mb-8">
          <img src="/logo.png" alt="SMART Momentum" className="w-10 h-10 rounded-xl glow-emerald" />
          <span className="font-display text-xl font-extrabold tracking-tight" data-testid="brand-wordmark">
            <span className="text-emerald-400">SMART</span> Momentum
          </span>
        </div>

        {user && (
          <div className="mb-6">
            <LevelBadge compact level={user.level} progress={user.level_progress} need={user.level_need} xp={user.xp} />
          </div>
        )}

        <nav className="space-y-1 flex-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-colors ${
                  isActive ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "text-white hover:text-white hover:bg-white/5"
                }`
              }
            >
              <l.icon className="w-5 h-5" />
              {l.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin-tab"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-colors ${
                  isActive ? "bg-amber-500/10 text-amber-400 border border-amber-500/30" : "text-white hover:bg-white/5"
                }`
              }
            >
              <ShieldCheck className="w-5 h-5" />
              Admin
            </NavLink>
          )}
        </nav>

        {user && !user.is_premium && user.role !== "admin" && (
          <Link
            to="/upgrade"
            data-testid="sidebar-upgrade"
            className="mb-4 flex items-center gap-2 justify-center h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold hover:opacity-90 transition-opacity"
          >
            <Crown className="w-4 h-4" />
            {user.on_trial ? `${user.trial_days_left}d left · Upgrade` : "Upgrade to Pro"}
          </Link>
        )}
        <div className="border-t border-[#242417] pt-4">
          <NavLink
            to="/profile"
            data-testid="nav-profile-card"
            className={({ isActive }) =>
              `flex items-center gap-3 px-2 py-2 mb-2 rounded-xl transition-colors ${isActive ? "bg-emerald-500/10" : "hover:bg-white/5"}`
            }
          >
            <Avatar
              userId={user?.id}
              name={user?.name}
              email={user?.email}
              hasAvatar={user?.has_avatar}
              version={user?.avatar_version}
              className="w-9 h-9"
              textClassName="text-sm"
              rounded="rounded-full"
            />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-white truncate">View profile</div>
            </div>
          </NavLink>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-white hover:text-red-400 hover:bg-red-500/10 transition-colors text-sm font-medium"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile top-right avatar → profile */}
      <NavLink
        to="/profile"
        data-testid="nav-profile-mobile"
        className="md:hidden fixed top-4 right-4 z-40 w-10 h-10 rounded-full shadow-lg"
      >
        <Avatar
          userId={user?.id}
          name={user?.name}
          email={user?.email}
          hasAvatar={user?.has_avatar}
          version={user?.avatar_version}
          className="w-10 h-10"
          textClassName="text-base"
          rounded="rounded-full"
        />
      </NavLink>

      {/* Mobile bottom bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0D0F17]/95 backdrop-blur-md border-t border-[#242417] flex items-center justify-around h-16 px-0.5">
        {LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            data-testid={`${l.testid}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-1.5 py-1.5 rounded-lg text-[10px] font-medium whitespace-nowrap leading-none ${isActive ? "text-emerald-400" : "text-white"}`
            }
          >
            <l.icon className="w-5 h-5" />
            {l.label}
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <NavLink
            to="/admin"
            data-testid="nav-admin-tab-mobile"
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-1.5 py-1.5 rounded-lg text-[10px] font-medium whitespace-nowrap leading-none ${isActive ? "text-amber-400" : "text-white"}`
            }
          >
            <ShieldCheck className="w-5 h-5" />
            Admin
          </NavLink>
        )}
      </nav>
    </>
  );
}
